import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import WatchlistReportService, { ReportAlumniRow, ReportResponse } from './WatchlistReportService';
import WatchlistReportTable, { RowAction } from './WatchlistReportTable';
import { MarkPitchReadyModal, ChangePhaseModal, MarkDormantModal, AdvanceStageModal } from './ActionModals';
import { CHIP_DIMENSIONS, LAYER_LABELS, OUTCOME_LABELS, STAGES, STAGE_LABELS, watchlistCodeOptions } from './labelMaps';

/**
 * Watchlist Alumni Report (spec §9). The page opens from any dashboard card's "View All →" link
 * with pre-filled URL params. URL is the source of truth — every filter lives in the query string
 * (bookmarkable / shareable, back-forward works). Internal-admin-only (backend enforces).
 */
type ModalState =
    | { kind: 'markReady' | 'changePhase' | 'markDormant' | 'advanceStage' | 'markDenied'; row: ReportAlumniRow }
    | null;

const WatchlistReportPage = () => {
    const [params, setParams] = useSearchParams();
    const instituteId = params.get('institute_id');
    const filterInstituteId = params.get('filter_institute_id');

    const [data, setData] = useState<ReportResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modal, setModal] = useState<ModalState>(null);
    const [programs, setPrograms] = useState<string[]>([]);
    const [batchYears, setBatchYears] = useState<number[]>([]);

    const layer = params.get('layer');

    // Program / Batch-year dropdown options — scoped to the selected institute / group (and campus filter).
    useEffect(() => {
        if (!instituteId) return;
        WatchlistReportService.getFilterOptions(instituteId, filterInstituteId)
            .then((r) => { setPrograms(r.programs || []); setBatchYears(r.batch_years || []); })
            .catch(() => { setPrograms([]); setBatchYears([]); });
    }, [instituteId, filterInstituteId]);

    // Build the filters object straight from the URL.
    const filters = useMemo(() => {
        const f: Record<string, any> = {};
        params.forEach((v, k) => { f[k] = v; });
        return f;
    }, [params]);

    const fetchData = useCallback(() => {
        if (!instituteId) { setError('No institute selected.'); setLoading(false); return; }
        setLoading(true);
        setError(null);
        WatchlistReportService.getReport(filters as any)
            .then(setData)
            .catch((e) => setError(e?.response?.data?.error?.message || e?.message || 'Failed to load report.'))
            .finally(() => setLoading(false));
    }, [filters, instituteId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Mutate one URL param (resets to page 1). Empty value removes it.
    const setFilter = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        if (value === '' || value == null) next.delete(key);
        else next.set(key, value);
        if (key !== 'page') next.delete('page');
        // Changing layer invalidates a watchlist_code/stage chosen for the old layer.
        if (key === 'layer') { next.delete('watchlist_code'); if (value !== 'activation') next.delete('stage'); }
        setParams(next);
    };

    const clearAll = () => {
        const next = new URLSearchParams();
        if (instituteId) next.set('institute_id', instituteId);
        const fi = params.get('filter_institute_id');
        if (fi) next.set('filter_institute_id', fi);
        setParams(next);
    };

    const activeChips = CHIP_DIMENSIONS.filter((d) => params.get(d.key));

    const refresh = () => { setModal(null); fetchData(); };

    // Actions without their own modal hit the API directly, then refresh.
    const onAction = async (action: RowAction, row: ReportAlumniRow) => {
        const inst = row.institute_id as number;
        try {
            if (action === 'markReady') return setModal({ kind: 'markReady', row });
            if (action === 'changePhase') return setModal({ kind: 'changePhase', row });
            if (action === 'markDormant') return setModal({ kind: 'markDormant', row });
            if (action === 'advanceStage') return setModal({ kind: 'advanceStage', row });
            if (action === 'markDenied') return setModal({ kind: 'markDenied', row });
            if (action === 'unmarkReady') { await WatchlistReportService.unmarkPitchReady({ institute_id: inst, student_id: row.student_id, watchlist_code: row.watchlist_code }); return fetchData(); }
            if (action === 'markActive') { await WatchlistReportService.markActive({ institute_id: inst, student_id: row.student_id, layer: row.layer, outcome: row.outcome }); return fetchData(); }
            if (action === 'clearOverride') { await WatchlistReportService.clearOverride({ institute_id: inst, student_id: row.student_id, layer: row.layer, outcome: row.outcome }); return fetchData(); }
            if (action === 'viewProfile' || action === 'addToCustom') {
                // Out of scope for v1 (separate features). No-op placeholder.
                return;
            }
        } catch (e: any) {
            setError(e?.response?.data?.error?.message || e?.message || 'Action failed.');
        }
    };

    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
        setExporting(true);
        try {
            const blob = await WatchlistReportService.exportCSV(filters as any);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `watchlist-report-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e: any) {
            setError(e?.response?.data?.error?.message || e?.message || 'Export failed.');
        } finally {
            setExporting(false);
        }
    };

    const page = data?.page ?? 1;
    const totalPages = data?.total_pages ?? 1;

    if (!instituteId) return <div className="main p-4"><div className="alert alert-danger">institute_id is required in the URL.</div></div>;

    return (
        <div className="main p-3 p-md-4">
            <div className="max-1140 mx-auto">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div>
                        <h2 className="m-0 font-22 font-600 text-dark-blue">Watchlist Alumni Report</h2>
                        <p className="m-0 font-13 text-secondary-emphasis">{data ? `${data.total_count} alumni` : '…'} matching the current filters</p>
                    </div>
                    <button className="btn btn-outline-primary rounded-3 font-13" onClick={handleExport} disabled={exporting}>
                        {exporting ? 'Exporting…' : '⬇ Export CSV'}
                    </button>
                </div>

                {/* Filter bar */}
                <div className="bg-white border rounded-3 p-3 mb-3 shadow-sm">
                    <div className="row g-2">
                        <div className="col-md-3"><input className="form-control form-control-sm rounded-3 font-13" placeholder="Search name…" defaultValue={params.get('search') || ''}
                            onKeyDown={(e) => { if (e.key === 'Enter') setFilter('search', (e.target as HTMLInputElement).value); }} /></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={layer || ''} onChange={(e) => setFilter('layer', e.target.value)}>
                            <option value="">All layers</option>
                            {Object.entries(LAYER_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select></div>
                        <div className="col-md-3"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('watchlist_code') || ''} onChange={(e) => setFilter('watchlist_code', e.target.value)}>
                            <option value="">All watchlists</option>
                            {watchlistCodeOptions(layer).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('outcome') || ''} onChange={(e) => setFilter('outcome', e.target.value)}>
                            <option value="">All outcomes</option>
                            {Object.entries(OUTCOME_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('stage') || ''} disabled={layer !== 'activation'} onChange={(e) => setFilter('stage', e.target.value)}>
                            <option value="">{layer === 'activation' ? 'All stages' : 'Stage (L3 only)'}</option>
                            {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('program') || ''} onChange={(e) => setFilter('program', e.target.value)}>
                            <option value="">All programs</option>
                            {programs.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('batch_year') || ''} onChange={(e) => setFilter('batch_year', e.target.value)}>
                            <option value="">All batch years</option>
                            {batchYears.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('alma_mater_pride') || ''} onChange={(e) => setFilter('alma_mater_pride', e.target.value)}>
                            <option value="">Alma Mater Pride: any</option>
                            <option value="HIGH">High</option><option value="MODERATE">Moderate</option><option value="LOW">Low</option>
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('marketability') || ''} onChange={(e) => setFilter('marketability', e.target.value)}>
                            <option value="">Marketability: any</option>
                            <option value="HIGH">High</option><option value="MODERATE">Moderate</option><option value="LOW">Low</option>
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('vibrancy') || ''} onChange={(e) => setFilter('vibrancy', e.target.value)}>
                            <option value="">Vibrancy: any</option>
                            <option value="HIGH">High</option><option value="MODERATE">Moderate</option><option value="LOW">Low</option>
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('connected') || ''} onChange={(e) => setFilter('connected', e.target.value)}>
                            <option value="">Connected: any</option>
                            <option value="YES">Yes</option><option value="NO">No</option>
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('recognized') || ''} onChange={(e) => setFilter('recognized', e.target.value)}>
                            <option value="">Recognized: any</option>
                            <option value="YES">Yes</option><option value="NO">No</option>
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('engaged') || ''} onChange={(e) => setFilter('engaged', e.target.value)}>
                            <option value="">Engaged: any</option>
                            <option value="YES">Yes</option><option value="NO">No</option>
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('pitch_ready') || ''} onChange={(e) => setFilter('pitch_ready', e.target.value)}>
                            <option value="">Pitch: any</option><option value="yes">Pitch ready</option><option value="no">Not ready</option>
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('reachout_phase') || ''} onChange={(e) => setFilter('reachout_phase', e.target.value)}>
                            <option value="">All phases</option>
                            <option value="1">Phase 1</option>
                            <option value="2">Phase 2</option>
                            <option value="3">Phase 3</option>
                            <option value="not_assigned">Not assigned</option>
                        </select></div>
                        <div className="col-md-2"><select className="form-select form-select-sm rounded-3 font-13" value={params.get('sort') || 'ability_desc'} onChange={(e) => setFilter('sort', e.target.value)}>
                            <option value="ability_desc">Ability ↓</option><option value="ability_asc">Ability ↑</option>
                            <option value="name_asc">Name A–Z</option><option value="date_desc">Newest</option>
                        </select></div>
                    </div>
                </div>

                {/* Chips */}
                {activeChips.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        {activeChips.map((d) => {
                            const raw = params.get(d.key) as string;
                            return (
                                <span key={d.key} className="badge bg-primary bg-opacity-10 text-primary font-11 d-inline-flex align-items-center gap-1">
                                    {d.label}: {d.format ? d.format(raw) : raw}
                                    <i className="bi bi-x cursor-pointer" onClick={() => setFilter(d.key, '')}></i>
                                </span>
                            );
                        })}
                        <button className="btn btn-sm btn-link text-decoration-none font-11 p-0" onClick={clearAll}>Clear All Filters</button>
                    </div>
                )}

                {error && <div className="alert alert-danger font-13">{error}</div>}
                {loading ? (
                    <div className="font-14 text-secondary-emphasis py-4">Loading report…</div>
                ) : data && (
                    <>
                        <WatchlistReportTable rows={data.alumni} onAction={onAction} />
                        <div className="d-flex align-items-center justify-content-between mt-3">
                            <span className="font-12 text-muted">Page {page} of {totalPages} · {data.total_count} total</span>
                            <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-secondary rounded-3" disabled={page <= 1} onClick={() => setFilter('page', String(page - 1))}>← Prev</button>
                                <button className="btn btn-sm btn-outline-secondary rounded-3" disabled={page >= totalPages} onClick={() => setFilter('page', String(page + 1))}>Next →</button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {modal?.kind === 'markReady' && <MarkPitchReadyModal row={modal.row} onClose={() => setModal(null)} onDone={refresh} />}
            {modal?.kind === 'changePhase' && <ChangePhaseModal row={modal.row} onClose={() => setModal(null)} onDone={refresh} />}
            {modal?.kind === 'markDormant' && <MarkDormantModal row={modal.row} onClose={() => setModal(null)} onDone={refresh} />}
            {modal?.kind === 'advanceStage' && <AdvanceStageModal row={modal.row} onClose={() => setModal(null)} onDone={refresh} />}
            {modal?.kind === 'markDenied' && <AdvanceStageModal row={modal.row} denied onClose={() => setModal(null)} onDone={refresh} />}
        </div>
    );
};

export default WatchlistReportPage;
